import type { PrerequisiteCourseBlock } from './course-prerequisite.js';

export const ROLES = {
  DEVELOPER: 'developer',
  SUPER_ADMIN: 'super-admin',
  ADMIN: 'admin',
  TEACHER: 'teacher',
  STUDENT: 'student',
  SEREMITT: 'seremitt',
} as const;

export type RoleSlug = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_LABELS: Record<RoleSlug, string> = {
  developer: 'Desarrollador',
  'super-admin': 'Super Administrador',
  admin: 'Administrador',
  teacher: 'Profesor',
  student: 'Estudiante',
  seremitt: 'SEREMITT',
};
export const PERMISSIONS = {
  DASHBOARD_ACCESS: 'dashboard.access',
  USUARIOS_VER: 'usuarios.ver',
  USUARIOS_CREAR: 'usuarios.crear',
  USUARIOS_EDITAR: 'usuarios.editar',
  USUARIOS_ELIMINAR: 'usuarios.eliminar',
  ESTUDIANTES_PERFIL_VER: 'estudiantes.perfil.ver',
  CAMPOS_USUARIO_VER: 'campos-usuario.ver',
  CAMPOS_USUARIO_CREAR: 'campos-usuario.crear',
  CAMPOS_USUARIO_EDITAR: 'campos-usuario.editar',
  CAMPOS_USUARIO_ELIMINAR: 'campos-usuario.eliminar',
  ROLES_VER: 'roles.ver',
  ROLES_CREAR: 'roles.crear',
  ROLES_EDITAR: 'roles.editar',
  ROLES_ELIMINAR: 'roles.eliminar',
  PERMISOS_VER: 'permisos.ver',
  PERMISOS_EDITAR: 'permisos.editar',
  PERFIL_VER: 'perfil.ver',
  PERFIL_EDITAR: 'perfil.editar',
  CATEGORIAS_VER: 'categorias.ver',
  CATEGORIAS_CREAR: 'categorias.crear',
  CATEGORIAS_EDITAR: 'categorias.editar',
  CATEGORIAS_ELIMINAR: 'categorias.eliminar',
  CURSOS_VER: 'cursos.ver',
  CURSOS_CREAR: 'cursos.crear',
  CURSOS_EDITAR: 'cursos.editar',
  CURSOS_ELIMINAR: 'cursos.eliminar',
  CURSOS_SUPERVISAR: 'cursos.supervisar',
  BIBLIOTECA_VER: 'biblioteca.ver',
  BIBLIOTECA_CREAR: 'biblioteca.crear',
  BIBLIOTECA_EDITAR: 'biblioteca.editar',
  BIBLIOTECA_ELIMINAR: 'biblioteca.eliminar',
  CONSTRUCTOR_VER: 'constructor.ver',
  CONSTRUCTOR_EDITAR: 'constructor.editar',
  MULTIMEDIA_VER: 'multimedia.ver',
  MULTIMEDIA_CREAR: 'multimedia.crear',
  MULTIMEDIA_EDITAR: 'multimedia.editar',
  MULTIMEDIA_ELIMINAR: 'multimedia.eliminar',
  H5P_VER: 'h5p.ver',
  H5P_IMPORTAR: 'h5p.importar',
  H5P_EDITAR: 'h5p.editar',
  H5P_ELIMINAR: 'h5p.eliminar',
  H5P_PUBLICAR: 'h5p.publicar',
  H5P_RESTAURAR: 'h5p.restaurar',
  H5P_DEPENDENCIAS_VER: 'h5p.dependencias.ver',
  EVALUACIONES_VER: 'evaluaciones.ver',
  EVALUACIONES_INTENTAR: 'evaluaciones.intentar',
  EVALUACIONES_CREAR: 'evaluaciones.crear',
  EVALUACIONES_EDITAR: 'evaluaciones.editar',
  EVALUACIONES_ELIMINAR: 'evaluaciones.eliminar',
  EVALUACIONES_PUBLICAR: 'evaluaciones.publicar',
  EVALUACIONES_RESTAURAR: 'evaluaciones.restaurar',
  EVALUACIONES_INTENTOS_GESTIONAR: 'evaluaciones.intentos.gestionar',
  PREGUNTAS_VER: 'preguntas.ver',
  PREGUNTAS_CREAR: 'preguntas.crear',
  PREGUNTAS_EDITAR: 'preguntas.editar',
  PREGUNTAS_ELIMINAR: 'preguntas.eliminar',
  PROGRESO_VER: 'progreso.ver',
  PROGRESO_TIEMPO_VER: 'progreso.tiempo.ver',
  PROGRESO_ADMINISTRAR: 'progreso.administrar',
  MIS_CURSOS_VER: 'mis-cursos.ver',
  CALIFICACIONES_VER: 'calificaciones.ver',
  CALIFICACIONES_ADMINISTRAR: 'calificaciones.administrar',
  CALIFICACIONES_EDITAR: 'calificaciones.editar',
  EXPEDIENTE_VER: 'expediente.ver',
  EXPEDIENTE_VER_TODOS: 'expediente.ver-todos',
  EXPEDIENTE_ADMINISTRAR: 'expediente.administrar',
  INSCRIPCIONES_VER: 'inscripciones.ver',
  INSCRIPCIONES_ADMINISTRAR: 'inscripciones.administrar',
  INSCRIPCIONES_ASIGNAR: 'inscripciones.asignar',
  GRUPOS_VER: 'grupos.ver',
  GRUPOS_ADMINISTRAR: 'grupos.administrar',
  CREDENCIALES_VER: 'credenciales.ver',
  CREDENCIALES_ADMINISTRAR: 'credenciales.administrar',
  CREDENCIALES_EMITIR: 'credenciales.emitir',
  CREDENCIALES_PLANTILLAS: 'credenciales.plantillas',
  MIS_CREDENCIALES_VER: 'mis-credenciales.ver',
  CONTRATOS_VER: 'contratos.ver',
  CONTRATOS_PLANTILLAS: 'contratos.plantillas',
  CONTRATOS_ADMINISTRAR: 'contratos.administrar',
  MIS_CONTRATOS_VER: 'mis-contratos.ver',
  MIS_SOPORTE_VER: 'mis-soporte.ver',
  SOPORTE_VER: 'soporte.ver',
  SOPORTE_ADMINISTRAR: 'soporte.administrar',
  POLITICAS_LEGALES_VER: 'politicas-legales.ver',
  POLITICAS_LEGALES_ADMINISTRAR: 'politicas-legales.administrar',
  APARIENCIA_VER: 'apariencia.ver',
  APARIENCIA_ADMINISTRAR: 'apariencia.administrar',
  DESARROLLO_VER: 'desarrollo.ver',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/** Rol «Administrador»: apariencia, desarrollo y perfil. */
export const ADMIN_PERMISSIONS: Permission[] = [
  PERMISSIONS.DASHBOARD_ACCESS,
  PERMISSIONS.PERFIL_VER,
  PERMISSIONS.PERFIL_EDITAR,
  PERMISSIONS.APARIENCIA_VER,
  PERMISSIONS.DESARROLLO_VER,
];

/** Rol «SEREMITT»: supervisión de cursos en solo lectura. */
export const SEREMITT_PERMISSIONS: Permission[] = [
  PERMISSIONS.CURSOS_VER,
  PERMISSIONS.CURSOS_SUPERVISAR,
  PERMISSIONS.CONSTRUCTOR_VER,
  PERMISSIONS.EVALUACIONES_VER,
  PERMISSIONS.PREGUNTAS_VER,
  PERMISSIONS.INSCRIPCIONES_VER,
  PERMISSIONS.CALIFICACIONES_VER,
  PERMISSIONS.BIBLIOTECA_VER,
  PERMISSIONS.H5P_VER,
  PERMISSIONS.PROGRESO_VER,
  PERMISSIONS.PROGRESO_TIEMPO_VER,
  PERMISSIONS.EXPEDIENTE_VER_TODOS,
  PERMISSIONS.ESTUDIANTES_PERFIL_VER,
  PERMISSIONS.DASHBOARD_ACCESS,
  PERMISSIONS.PERFIL_VER,
];

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    lastPage: number;
  };
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  code?: string;
  contractId?: string | null;
  prerequisiteCourse?: PrerequisiteCourseBlock;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: RoleSlug[];
  permissions: Permission[];
  /** Nivel jerárquico efectivo (menor N = más privilegio). */
  hierarchyLevel?: number;
}

export interface LoginResponse {
  user: AuthUser;
  /** Solo para clientes CLI; la SPA usa cookies httpOnly. */
  accessToken?: string;
}
